
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Account, FinancialData, emptyChartData } from "../types";
import { getSubordinateIds } from "../utils/subordinateUtils";
import { getAccountOwnershipInfo } from "../../business-value-framework/accountService";

export const useFinancialData = (selectedAccount: Account | null) => {
  const [isLoadingFinancials, setIsLoadingFinancials] = useState(false);
  const [financialData, setFinancialData] = useState<FinancialData | null>(emptyChartData);
  const [companyNameInput, setCompanyNameInput] = useState("");
  const [rawFinancialResponse, setRawFinancialResponse] = useState<string>("");
  
  // Add error dialog state
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // Add supervisor detection states
  const [isOwner, setIsOwner] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [ownershipLoaded, setOwnershipLoaded] = useState(false);

  useEffect(() => {
    if (selectedAccount?.company_id) {
      loadFinancialData(selectedAccount.company_id);
    } else {
      // Reset all states when no account is selected
      setIsOwner(false);
      setCanEdit(false);
      setIsSupervisor(false);
      setOwnershipLoaded(false);
    }
  }, [selectedAccount?.company_id]);

  const loadFinancialData = async (companyId: string) => {
    try {
      setIsLoadingFinancials(true);
      
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        toast.error("User not authenticated");
        setIsLoadingFinancials(false);
        return;
      }

      // Get account ownership information
      const ownershipInfo = await getAccountOwnershipInfo(companyId);
      
      console.log("Account ownership info:", ownershipInfo);

      // Determine final ownership states
      const userIsOwner = ownershipInfo.isOwner;
      const userCanEdit = ownershipInfo.canEdit;
      
      // Only set as supervisor if user is NOT the owner AND can view the account
      // This means they have supervisor privileges through subordinate relationship
      let userIsSupervisor = false;
      if (!userIsOwner && ownershipInfo.canView) {
        // Double-check by verifying the subordinate relationship
        const subordinates = await getSubordinateIds(userId);
        userIsSupervisor = subordinates.includes(ownershipInfo.accountOwnerUserId || '');
      }
      
      console.log("User ownership status:", { userIsOwner, userCanEdit, userIsSupervisor });
      
      // Set all states atomically to prevent race conditions
      setIsOwner(userIsOwner);
      setCanEdit(userCanEdit);
      setIsSupervisor(userIsSupervisor);
      setOwnershipLoaded(true);

      // Determine which user's data to load
      const targetUserId = userIsOwner 
        ? userId 
        : ownershipInfo.accountOwnerUserId;

      if (!targetUserId) {
        console.error("No target user ID found");
        setIsLoadingFinancials(false);
        return;
      }

      console.log("Loading financial data for user:", targetUserId);
      
      let { data, error } = await supabase
        .from('finance_summary')
        .select('*')
        .eq('company_id', companyId)
        .eq('user_id', targetUserId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (!data && !error) {
        const subordinateIds = await getSubordinateIds(userId);
        if (subordinateIds.length > 0) {
          const { data: subordinateData, error: subordinateError } = await supabase
            .from('finance_summary')
            .select('*')
            .eq('company_id', companyId)
            .in('user_id', subordinateIds)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
            
          if (subordinateError) {
            console.error("Error fetching subordinate financial data:", subordinateError);
            setIsLoadingFinancials(false);
            return;
          }
          
          data = subordinateData;
        }
      }
      
      if (error) {
        console.error("Error fetching financial data:", error);
        setIsLoadingFinancials(false);
        return;
      }
      
      if (data) {
        const rawLabels = Array.isArray(data.fiscal_years) ? data.fiscal_years.map(year => String(year)) : [];
        const rawRevenue = Array.isArray(data.revenue) ? data.revenue.map(value => Number(value)) : [];
        const rawProfit = Array.isArray(data.gross_profit) ? data.gross_profit.map(value => Number(value)) : [];
        const rawEps = Array.isArray(data.eps) ? data.eps.map(value => Number(value)) : [];
        const rawCashFlow = Array.isArray(data.free_cash_flow) ? data.free_cash_flow.map(value => Number(value)) : [];
        const rawDebt = Array.isArray(data.total_debt) ? data.total_debt.map(value => Number(value)) : [];
        const rawEbitdaMargins = Array.isArray(data.ebitda_margins) ? data.ebitda_margins.map(value => Number(value)) : [];

        // Filter out years with missing or zero data
        const validIndices = [];
        for (let i = 0; i < rawLabels.length; i++) {
          const hasValidData = (rawRevenue[i] > 0) || 
                             (rawProfit[i] > 0) || 
                             (rawEps[i] > 0) || 
                             (rawCashFlow[i] > 0) || 
                             (rawDebt[i] > 0) || 
                             (rawEbitdaMargins[i] > 0);
          
          if (hasValidData) {
            validIndices.push(i);
          }
        }

        const processedData: FinancialData = {
          labels: validIndices.map(i => rawLabels[i]),
          revenue: validIndices.map(i => rawRevenue[i]),
          profit: validIndices.map(i => rawProfit[i]),
          eps: validIndices.map(i => rawEps[i]),
          cashFlow: validIndices.map(i => rawCashFlow[i]),
          debt: validIndices.map(i => rawDebt[i]),
          ebitdaMargins: validIndices.map(i => rawEbitdaMargins[i])
        };
        
        setFinancialData(processedData);
        setCompanyNameInput(data.company_symbol);
        setRawFinancialResponse(`Loaded saved financial data for ${data.company_symbol}`);
        toast.success(`Loaded saved financial data for ${data.company_symbol}`);
      }
      
      setIsLoadingFinancials(false);
    } catch (error) {
      console.error("Error:", error);
      setIsLoadingFinancials(false);
      // Reset all states on error
      setIsOwner(false);
      setCanEdit(false);
      setIsSupervisor(false);
      setOwnershipLoaded(false);
    }
  };

  const saveFinancialData = async (companySymbol: string, processedData: FinancialData) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId || !selectedAccount?.company_id) {
        console.error("Missing user ID or company ID");
        return;
      }

      // Get account ownership information
      const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
      
      // Only allow account owners to save financial data
      if (!ownershipInfo.canEdit) {
        toast.error("Only account owners can save financial data");
        return;
      }

      // Only the account owner can create/update records in their name
      if (!ownershipInfo.isOwner) {
        toast.error("Only account owners can modify financial data");
        return;
      }
      
      const { data: existingData } = await supabase
        .from('finance_summary')
        .select('id')
        .eq('company_id', selectedAccount.company_id)
        .eq('user_id', userId)
        .eq('company_symbol', companySymbol)
        .maybeSingle();
      
      const financeData = {
        user_id: userId,
        company_id: selectedAccount.company_id,
        company_symbol: companySymbol,
        fiscal_years: processedData.labels,
        revenue: processedData.revenue,
        gross_profit: processedData.profit,
        ebitda_margins: processedData.ebitdaMargins,
        eps: processedData.eps,
        free_cash_flow: processedData.cashFlow,
        total_debt: processedData.debt
      };
      
      let result;
      
      if (existingData) {
        result = await supabase
          .from('finance_summary')
          .update(financeData)
          .eq('id', existingData.id);
      } else {
        result = await supabase
          .from('finance_summary')
          .insert([financeData]);
      }
      
      if (result.error) {
        console.error("Error saving financial data:", result.error);
        toast.error("Failed to save financial data");
      } else {
        console.log("Financial data saved successfully");
      }
    } catch (error) {
      console.error("Error saving financial data:", error);
    }
  };

  const fetchFinancialData = async (companyName: string) => {
    if (!companyName) {
      const errorMsg = 'Please enter a company name';
      setRawFinancialResponse(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    if (!selectedAccount?.company_id) {
      toast.error("Please select an account first");
      return;
    }
    
    setIsLoadingFinancials(true);
    setRawFinancialResponse(`Fetching financial data for ${companyName}...`);
    
    try {
      const companySymbol = companyName.split(' ')[0].toUpperCase();
      
      console.log(`Fetching financial data via Perplexity for symbol: ${companySymbol}`);
      
      const { data, error } = await supabase.functions.invoke('search-financial-data', {
        body: { companySymbol }
      });

      if (error) {
        throw new Error(error.message || 'Edge function call failed');
      }

      if (!data?.success || !data?.data) {
        throw new Error(data?.error || 'No financial data returned');
      }

      const result = data.data;

      console.log(`Received financial data for ${companySymbol}:`, result);

      const processedData: FinancialData = {
        labels: result.labels || [],
        revenue: result.revenue || [],
        profit: result.grossProfit || [],
        eps: result.eps || [],
        cashFlow: result.freeCashFlow || [],
        debt: result.totalDebt || [],
        ebitdaMargins: result.ebitdaMargins || []
      };

      // Filter out years with all-zero data
      const validIndices: number[] = [];
      for (let i = 0; i < processedData.labels.length; i++) {
        const hasValidData = (processedData.revenue[i] > 0) || 
                           (processedData.profit[i] > 0) || 
                           (Math.abs(processedData.eps[i]) > 0) || 
                           (Math.abs(processedData.cashFlow[i]) > 0) || 
                           (processedData.debt[i] > 0) || 
                           (Math.abs(processedData.ebitdaMargins[i]) > 0);
        if (hasValidData) {
          validIndices.push(i);
        }
      }

      if (validIndices.length === 0) {
        throw new Error('No valid financial data available for any year.');
      }

      const filteredData: FinancialData = {
        labels: validIndices.map(i => processedData.labels[i]),
        revenue: validIndices.map(i => processedData.revenue[i]),
        profit: validIndices.map(i => processedData.profit[i]),
        eps: validIndices.map(i => processedData.eps[i]),
        cashFlow: validIndices.map(i => processedData.cashFlow[i]),
        debt: validIndices.map(i => processedData.debt[i]),
        ebitdaMargins: validIndices.map(i => processedData.ebitdaMargins[i])
      };

      console.log(`Processed financial data for ${companySymbol}:`, filteredData);

      setFinancialData(filteredData);
      setRawFinancialResponse(`Successfully retrieved financial data for ${companyName}`);
      toast.success(`Financial data fetched for ${companyName}`);
      
      await saveFinancialData(companySymbol, filteredData);
      
    } catch (error) {
      console.error('Error fetching financial data:', error);
      
      setFinancialData(emptyChartData);
      
      const errorMessage = error.message || 'Unknown error occurred';
      setRawFinancialResponse(`Error fetching financial data: ${errorMessage}`);
      
      setErrorMessage(`Unable to fetch financial data for "${companyName}". ${errorMessage}`);
      setShowErrorDialog(true);
    } finally {
      setIsLoadingFinancials(false);
    }
  };

  return {
    isLoadingFinancials,
    financialData,
    companyNameInput,
    setCompanyNameInput,
    rawFinancialResponse,
    fetchFinancialData,
    isOwner,
    canEdit,
    isSupervisor,
    ownershipLoaded,
    showErrorDialog,
    setShowErrorDialog,
    errorMessage
  };
};
